import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '@processo/services/user.service';
import { NgZorroAntdModule } from '@processo/shared/ng-zorro-antd.module';
import { SharedModule } from '@processo/shared/shared.module';
import { extractEntityName, setDefaultProcessToState } from '@processo/shared/state/appstate.logic';
import { AppstateService } from '@processo/shared/state/appstate.service';
import { StateService } from '@processo/shared/state/state.service';
import { Subscription, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import {
  preloadedContactsCache,
  setPendingConversationDrilldown,
} from '@processo/shared/components/entity-layout/smart-table/cache';
import { clearAuthStorage } from '@processo/core/helpers/storage.util';
import { logAuthEvent, describeHttpError } from '@processo/core/helpers/auth-diag.util';
interface payload {
  org_id: string,
  process_id: string,
}
@Component({
    selector: 'app-loading',
    templateUrl: './loading.component.html',
    styleUrls: ['./loading.component.scss'],
    imports: [NgZorroAntdModule, CommonModule, SharedModule]
})
export class LoadingComponent implements OnInit, OnDestroy {
  router = inject(Router);
  route=inject(ActivatedRoute)
  stateService = inject(StateService);
  appStateService = inject(AppstateService);
  userService = inject(UserService);
  isRedirect: string = '';
  loadFailed = false;
  isBootstrapping = false;
  private loadSub: Subscription | null = null;
  constructor() {
    this.route.queryParams.subscribe(params => {
      this.isRedirect = params['redirect'] || '';
    });
  }
  ngOnInit(): void {
    this.initial_load();
  }
  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
  }
  canShowManualLogout(): boolean {
    return this.isMobile() || this.loadFailed;
  }
  retryBootstrap(): void {
    logAuthEvent('loading-bootstrap-retry');
    this.initial_load();
  }
  manualLogout(): void {
    logAuthEvent('loading-manual-logout');
    clearAuthStorage();
    this.appStateService.resetAppState();
    sessionStorage.clear();
    this.router.navigateByUrl('/login');
  }
  private isMobile(): boolean {
    if (typeof window === 'undefined') return false;
    return this.appStateService.is_mobile() || window.innerWidth <= 800;
  }
  initial_load() {
    if (this.isBootstrapping) {
      return;
    }
    this.isBootstrapping = true;
    this.loadFailed = false;
    this.loadSub?.unsubscribe();

    // Invite pending is nice-to-have; a 504/CORS failure must not abort org bootstrap.
    const __inviteCall = this.isMobile()
      ? of(null)
      : this.userService.get_invite_user_pending().pipe(
          catchError((err) => {
            logAuthEvent('invite-pending-failed', describeHttpError(err));
            return of(null);
          }),
        );
    this.loadSub = combineLatest([__inviteCall, this.userService.getUserOrgProcess()])
      .pipe(take(1))
      .subscribe({
      next: async (response) => {
        this.isBootstrapping = false;
        const inviteUserResponse = response[0];
        const orgProcessResponse = response[1];
        let organizations = orgProcessResponse[0]?.org;
        try {
          if (!organizations || organizations.length === 0) {
            await this.setinvitationListToStore(inviteUserResponse);
            console.error('No organization found for the user');
            this.router.navigateByUrl(`/org/no-org`);
          } else {
            try {
              organizations = organizations.map((_o: any) => ({
                ..._o,
                public___org_processes: (_o?.public___org_processes || [])
                  .filter((x: any) => x && x.d_process_name)  // Filter out null processes
                  .map((x: any) => ({
                    ...x,
                    router_process_value: x.d_process_name
                  })),
                router_org_value: _o?.org_name
              }));
              await this.setOrganizationListToStore(organizations);
              await this.setinvitationListToStore(inviteUserResponse);
              await setDefaultProcessToState({ appStateService: this.appStateService, stateService: this.stateService });

              // Treat '/' and auth-entry routes as no real redirect target:
              // navigating to those just bounces login ↔ loading in a loop.
              const usableRedirect = this.sanitizeRedirect(this.isRedirect);
              if (usableRedirect) {
                const decodedRedirect = decodeURIComponent(usableRedirect);
                const orgName = decodeURIComponent(
                  await extractEntityName(decodedRedirect, 'org')
                );
                const process_name = decodeURIComponent(
                  await extractEntityName(decodedRedirect, 'process')
                );
                
                const org = organizations.find((e: any) => 
                  e.router_org_value === orgName || 
                  e.org_name === orgName ||
                  decodeURIComponent(e.router_org_value || '') === orgName
                );
                
                if (org) {
                  this.appStateService.updateDefaultOrganization(org);
                  const process = org?.public___org_processes.find((p: any) => 
                    p.router_process_value == process_name ||
                    p.process_name === process_name ||
                    p.d_process_name === process_name ||
                    decodeURIComponent(p.router_process_value || '') === process_name
                  );
                  
                  if (process) {
                    this.appStateService.updateDefaultProcess(process);
                    
                    // Extract the route path (conversation, view, action, etc.) from the redirect URL
                    const urlMatch = decodedRedirect.match(/\/org\/[^\/]+\/process\/[^\/]+\/([^?]+)/);
                    const routePath = urlMatch ? urlMatch[1] : 'view';
                    
                    // If conversation route, preload metadata
                    if (routePath === 'conversation' || routePath.startsWith('conversation')) {
                      this.setPendingDrilldownFromUrl(decodedRedirect, org, process);
                      this.preloadMetadataAndLayout(org, process, decodedRedirect)
                        .pipe(take(1))
                        .subscribe({
                          next: (res: any) => {
                            if (res) {
                              this.appStateService.updateMetaDataAndLayout(
                                res,
                                org.router_org_value || org.org_name,
                                process.router_process_value || process.process_name || process.d_process_name
                              );
                            }
                            this.router.navigateByUrl(usableRedirect);
                          },
                          error: () => {
                            this.router.navigateByUrl(usableRedirect);
                          },
                        });
                    } else {
                      // For other routes (view, action, etc.), preserve the full path
                      this.router.navigateByUrl(usableRedirect);
                    }
                  } else {
                    this.router.navigateByUrl(usableRedirect);
                  }
                } else {
                  this.router.navigateByUrl(usableRedirect);
                }
              } else {
                // Check if user is trying to navigate to a specific route
                const currentUrl = this.router.url;
                
                // Extract org and process from current URL if it's a valid route
                const urlMatch = currentUrl.match(/\/org\/([^\/]+)\/process\/([^\/]+)(?:\/([^?]+))?/);
                if (urlMatch) {
                  const urlOrgName = decodeURIComponent(urlMatch[1]);
                  const urlProcessName = decodeURIComponent(urlMatch[2]);
                  const urlPath = (urlMatch[3] || 'view').split('?')[0];
                  
                  const org = organizations.find((e: any) => 
                    e.router_org_value === urlOrgName || 
                    e.org_name === urlOrgName
                  );
                  
                  if (org) {
                    this.appStateService.updateDefaultOrganization(org);
                    
                    const process = org?.public___org_processes.find((p: any) => 
                      p.router_process_value === urlProcessName ||
                      p.process_name === urlProcessName ||
                      p.d_process_name === urlProcessName
                    );
                    
                    if (process) {
                      this.appStateService.updateDefaultProcess(process);
                      
                      // If conversation route, preload metadata
                      if (urlPath === 'conversation') {
                        const conversationUrl = currentUrl.includes('?')
                          ? currentUrl.slice(currentUrl.indexOf('/org/'))
                          : `/org/${urlOrgName}/process/${urlProcessName}/conversation`;
                        this.setPendingDrilldownFromUrl(conversationUrl, org, process);
                        this.preloadMetadataAndLayout(org, process, conversationUrl)
                          .pipe(take(1))
                          .subscribe({
                            next: (res: any) => {
                              if (res) {
                                this.appStateService.updateMetaDataAndLayout(
                                  res,
                                  org.router_org_value || org.org_name,
                                  process.router_process_value || process.process_name || process.d_process_name
                                );
                              }
                              this.router.navigateByUrl(conversationUrl);
                            },
                            error: () => {
                              this.router.navigateByUrl(conversationUrl);
                            },
                          });
                      } else {
                        this.router.navigateByUrl(`/org/${urlOrgName}/process/${urlProcessName}/${urlPath}`);
                      }
                      return;
                    }
                  }
                }
                
                // Fallback to default behavior
                const currentOrg = this.appStateService.getCurrentOrganization();
                const currentProcess = this.appStateService.getCurrentProcess();
                if (currentOrg && currentProcess) {
                  const orgName = currentOrg.router_org_value;
                  const processName = currentProcess.router_process_value;

                  // On mobile, preload metadata/layout like sidebar "conversation" click,
                  // then navigate to the org/process conversation route so APIs run as expected.
                  if (this.appStateService.is_mobile()) {
                    this.preloadMetadataAndLayout(currentOrg, currentProcess)
                      .pipe(take(1))
                      .subscribe({
                        next: (res: any) => {
                          if (res) {
                            this.appStateService.updateMetaDataAndLayout(
                              res,
                              currentOrg.router_org_value,
                              currentProcess.router_process_value
                            );
                          }
                          this.router.navigateByUrl(
                            `/org/${orgName}/process/${processName}/conversation`
                          );
                        },
                        error: () => {
                          this.router.navigateByUrl(
                            `/org/${orgName}/process/${processName}/conversation`
                          );
                        },
                      });
                  } else {
                    this.router.navigateByUrl(`/org/${orgName}/process/${processName}/view`);
                  }
                } else {
                  // Hard fallback: avoid staying forever on /loading when default state is missing.
                  // Re-derive a usable org/process from fetched organizations and continue.
                  const fallbackOrg = organizations?.[0];
                  const fallbackProcess = fallbackOrg?.public___org_processes?.[0];
                  if (fallbackOrg && fallbackProcess) {
                    this.appStateService.updateDefaultOrganization(fallbackOrg);
                    this.appStateService.updateDefaultProcess(fallbackProcess);
                    const orgName = fallbackOrg.router_org_value || fallbackOrg.org_name;
                    const processName =
                      fallbackProcess.router_process_value ||
                      fallbackProcess.process_name ||
                      fallbackProcess.d_process_name;
                    this.router.navigateByUrl(
                      this.appStateService.is_mobile()
                        ? `/org/${orgName}/process/${processName}/conversation`
                        : `/org/${orgName}/process/${processName}/view`
                    );
                  } else {
                    console.error('Current organization or process is not set');
                    this.handleBootstrapFailure({ reason: 'missing-default-org-process' });
                  }
                }
              }
            } catch (error) {
              console.error('Error during organization and process handling:', error);
            }
          }
        } catch (error) {
          console.error(error);
        }
      },
      error: (error) => {
        // Do NOT navigate to /login here: a still-valid session causes
        // LoginComponent to immediately restore back to /loading, which
        // re-fires getUserOrgProcess / invite and loops forever.
        logAuthEvent('loading-bootstrap-failed', describeHttpError(error));
        this.handleBootstrapFailure(describeHttpError(error));
      },
    });
  }

  private handleBootstrapFailure(details?: Record<string, unknown>): void {
    this.isBootstrapping = false;
    this.loadFailed = true;
    logAuthEvent('loading-bootstrap-stopped', details);
  }

  private sanitizeRedirect(raw: string | null | undefined): string | null {
    if (!raw || raw === '/') return null;
    let path = raw;
    try {
      path = decodeURIComponent(raw).split('?')[0];
    } catch {
      path = raw.split('?')[0];
    }
    const blocked = [
      '/loading',
      '/login',
      '/logout',
      '/contact-us',
      '/register',
      '/registernew',
      '/forgot-password',
    ];
    if (blocked.some((b) => path === b || path.startsWith(`${b}/`))) {
      return null;
    }
    return raw;
  }

  private setPendingDrilldownFromUrl(url: string, org: any, process: any): void {
    try {
      const query = url.includes('?') ? url.split('?')[1] : '';
      const str = new URLSearchParams(query).get('str')?.trim();
      if (!str) {
        return;
      }
      setPendingConversationDrilldown({
        org_id: org.org_id,
        process_id: process.process_id,
        str,
        autoSelectFirst: true,
      });
    } catch {
      // Ignore malformed deep-link query strings.
    }
  }

  private getConversationSearchStr(url?: string): string {
    if (!url) {
      return '';
    }
    try {
      const query = url.includes('?') ? url.split('?')[1] : '';
      return new URLSearchParams(query).get('str')?.trim().toLowerCase() || '';
    } catch {
      return '';
    }
  }

  private preloadMetadataAndLayout(org: any, process: any, redirectUrl?: string): Observable<any> {
    const payload = {
      org_id: org.org_id,
      process_id: process.process_id,
      org_process_id: process.org_process_id,
    };
    const metadataAndLayout$ = combineLatest([
      this.stateService.get_process_metadataNew(
        payload,
        process.process_name,
        process.org_process_id
      ),
      this.isMobile() ? of(null) : this.stateService.getLayoutMetadata(payload),
    ]);

    if (!this.isMobile()) {
      return metadataAndLayout$;
    }

    // On mobile: fire get_contact_details in parallel with get_process_metadata
    // so the total wait is max(contacts, metadata), not their sum. Cache the
    // contacts response on arrival so the conversation component can render it
    // without firing a second HTTP call.
    const contactBody = {
      org_id: org.org_id,
      process_id: process.process_id,
      entity_name: 'ALL',
      process_name: process.process_name?.replace(/ /g, '_'),
      limit: 50,
      skip: 0,
      str: this.getConversationSearchStr(redirectUrl),
      unread_msg: -1,
      call_disposition: -1,
    };
    const contacts$ = this.stateService.contactsDetailsSuccess(contactBody).pipe(
      tap((contactsRes: any) => {
        if (contactsRes) {
          preloadedContactsCache.set(
            this.buildContactsCacheKey(
              org.org_id,
              process.process_id,
              contactBody.str
            ),
            contactsRes
          );
        }
      }),
      catchError((err: any) => {
        return of(null);
      })
    );
    return combineLatest([contacts$, metadataAndLayout$]).pipe(
      map(([, metadataRes]) => metadataRes)
    );
  }

  private buildContactsCacheKey(
    orgId: string,
    processId: string,
    searchStr = ''
  ): string {
    // Matches the initial-load params the conversation component fires:
    // entity_name=ALL, skip=0, str=<search>, unread_msg=-1, call_disposition=-1
    return `${orgId}:${processId}:ALL:0:-1:-1:${searchStr}`;
  }

  private async setOrganizationListToStore(organizations: any) {
    this.appStateService.update_user_org_list(organizations);
  }
  private async setinvitationListToStore(data: any) {
    let value = data?.[0]?.invites;
    this.appStateService.update_user_org_invite(value);
  }
}
